import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { useI18n } from "@/lib/i18n";
import { ScrollArea } from "@/components/ui/scroll-area";
import noticeKr from "@/content/notice/notice_kr.md?raw";
import noticeEn from "@/content/notice/notice_en.md?raw";
import noticeJp from "@/content/notice/notice_jp.md?raw";

const notices: Record<string, string> = {
  ko: noticeKr,
  en: noticeEn,
  ja: noticeJp,
};

/** Simple Markdown → HTML renderer */
const MarkdownRenderer = ({ content }: { content: string }) => {
  const rendered = content
    .replace(/^### (.+)$/gm, '<h3 class="text-base font-bold mb-2 mt-4">$1</h3>')
    .replace(/^## (.+)$/gm, '<h2 class="text-lg font-bold mb-3 mt-6">$1</h2>')
    .replace(/^# (.+)$/gm, '<h1 class="text-xl font-bold mb-4">$1</h1>')
    .replace(/==(.+?)==/g, '<span class="text-primary font-bold">$1</span>')
    .replace(/^---$/gm, '<hr class="border-border/50 my-6" />')
    .replace(/\[(.+?)\]\((.+?)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" class="text-primary hover:underline font-medium">$1</a>')
    .replace(/\n\n/g, '<p class="mb-4"></p>')
    .replace(/\n/g, '<br/>');

  return (
    <div
      className="text-sm text-foreground/90 break-words leading-relaxed"
      dangerouslySetInnerHTML={{ __html: rendered }}
    />
  );
};

interface NoticeDialogProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

const NoticeDialog = ({ open: externalOpen, onOpenChange }: NoticeDialogProps) => {
  const { locale, t } = useI18n();
  const noticeContent = notices[locale] || notices.en;
  const [internalOpen, setInternalOpen] = useState(false);
  const [dontShowToday, setDontShowToday] = useState(false);

  const open = externalOpen !== undefined ? externalOpen : internalOpen;
  const setOpen = (val: boolean) => {
    if (onOpenChange) {
      onOpenChange(val);
    } else {
      setInternalOpen(val);
    }
  };

  useEffect(() => {
    // Only auto-open if not externally controlled and not dismissed for today
    if (externalOpen === undefined) {
      const lastNoticeDate = localStorage.getItem("lastNoticeDate");
      const today = new Date().toDateString();

      if (lastNoticeDate !== today) {
        setInternalOpen(true);
      }
    }
  }, [externalOpen]);

  const handleClose = () => {
    if (dontShowToday) {
      const today = new Date().toDateString();
      localStorage.setItem("lastNoticeDate", today);
    }
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-[600px] max-h-[80vh] flex flex-col glass-panel-static border-border/50">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-primary">{t('notice.title')}</DialogTitle>
        </DialogHeader>

        <ScrollArea className="flex-1 min-h-0 pr-4 mt-4">
          <div className="max-w-none">
            <MarkdownRenderer content={noticeContent} />
          </div>
        </ScrollArea>

        <DialogFooter className="flex-row items-center justify-between sm:justify-between mt-6 pt-4 border-t border-border/30">
          <div className="flex items-center space-x-2">
            <Checkbox
              id="dont-show-today"
              checked={dontShowToday}
              onCheckedChange={(checked) => setDontShowToday(checked === true)}
              className="border-primary/50 data-[state=checked]:bg-primary"
            />
            <Label
              htmlFor="dont-show-today"
              className="text-xs text-muted-foreground cursor-pointer hover:text-foreground transition-colors"
            >
              {t('notice.dont_show_today')}
            </Label>
          </div>
          <Button
            onClick={handleClose}
            size="sm"
            className="bg-primary hover:bg-primary/90 text-primary-foreground min-w-[80px]"
            data-testid="notice-confirm-button"
          >
            {t('notice.close')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default NoticeDialog;
